<script setup>
import { computed, ref, reactive, inject, onBeforeMount, onMounted, shallowRef } from 'vue';
import { useI18n } from 'vue-i18n';
import { useGlobalStore }from '../store/global';
import ErrorNote from './ErrorNote.vue';
// import iconClvr from '@client/svg/clvr.svg?raw';

const rootData = inject('rootData');
const { t } = useI18n();

// instance
const clover = shallowRef(null);

// card input wrappers
const inputs = {
  cardNumber: { // credit card number
    cloverInputId: 'CARD_NUMBER',
    key: 'cardNumber',
    visible: true,
    cloverElement: null,
  },
  cardHolderName: { // credit cardholder's name.
    cloverInputId: 'CARD_NAME',
    key: 'cardHolderName',
    visible: false,
    cloverElement: null,
  },
  cardDate: { // credit card's expiration date
    cloverInputId: 'CARD_DATE',
    key: 'cardDate',
    visible: true,
    cloverElement: null,
  },
  cardCvv: { // card verification number
    cloverInputId: 'CARD_CVV',
    key: 'cardCvv',
    visible: true,
    cloverElement: null,
  },
  cardPostalCode: { // cardholder's postal code
    cloverInputId: 'CARD_POSTAL_CODE',
    key: 'cardPostalCode',
    visible: true,
    cloverElement: null,
  },
  cardStreetAddress: { // cardholder's street address
    cloverInputId: 'CARD_STREET_ADDRESS',
    key: 'cardStreetAddress',
    visible: false,
    cloverElement: null,
  },
};

const visibleFields = Object.entries(inputs).filter(([, field]) => field.visible);

const touched = reactive({
  cardNumber: false,
  cardHolderName: false,
  cardDate: false,
  cardCvv: false,
  cardPostalCode: false,
  cardStreetAddress: false,
})
const errors = reactive({
  cardNumber: '',
  cardHolderName: '',
  cardDate: '',
  cardCvv: '',
  cardPostalCode: '',
  cardStreetAddress: '',
  token: '',
});

const errorsOrder = [
  'cardNumber',
  'cardHolderName',
  'cardDate',
  'cardCvv',
  'cardPostalCode',
  'cardStreetAddress',
  'token',
];

const firstError = computed(() => {
  for (const key of errorsOrder) {
    if (errors[key] && String(errors[key]).length > 0) {
      return errors[key];
    }
  }
  return '';
})

const isTouched = computed(() => {
  let touchedList = [];
  Object.keys(inputs).forEach(key => {
    if (inputs[key]?.visible) {
      touchedList.push(touched[key]);
    }
  });
  // console.log(touchedList.every(Boolean));
  return touchedList.every(Boolean);
});

const hasErrors = computed(() => {
  console.log('🍀 PaymentForm: isTouched %s , Errors: %o', isTouched.value, errors);
  return firstError.value.length > 0 || (!isTouched.value);
});

// expose for Checkout / Thank You Page payment form
defineExpose({
  clover,
  errors,
  firstError,
  hasErrors
});

const cloverInstanceSetup = () => {
  if (clover.value && window['Clover']) {
    const elements = clover.value.elements();
    const styles = {
      body: {
        fontFamily: 'Inter, Open Sans, sans-serif',
        fontSize: '16px'
      },
      input: {
        fontSize: '15px',
        padding: '0px',
        margin: '0px',
        backgroundColor: 'transparent',
        boxShadow: 'none',
        height: '1.875rem',
        borderRadius: '0.25rem',
      },
      'input(:-webkit-autofill, :autofill)': {
        backgroundColor: '-moz-autofill-background',
      },
      '.brand': {
        backgroundColor: 'red',
      }
    };

    Object.keys(inputs).forEach(key => {
      if (inputs[key]?.visible) {
        // create clover element
        inputs[key].cloverElement = elements.create(inputs[key].cloverInputId, styles);
        // mount clover element
        
        inputs[key].cloverElement.mount(`#${key}Input`);

        // process events on wrappers
        if (inputs[key].cloverElement) {

          ['change', 'blur'].forEach(eventName => {
            // handle real-time validation errors from the card Element.
            inputs[key].cloverElement.addEventListener(eventName, event => {
              switch (inputs[key].cloverElement.elementType) {
                case 'CARD_NUMBER':
                  touched.cardNumber = event?.CARD_NUMBER.touched;
                  errors.cardNumber = event?.CARD_NUMBER.error || (!event?.CARD_NUMBER.touched ? t('paymentForm.cardNumberRequired'): '') || '';
                  break;
                case 'CARD_NAME':
                  touched.cardNumber = event?.CARD_NAME.touched;
                  errors.cardHolderName = event?.CARD_NAME.error || (!event?.CARD_NAME.touched ? t('paymentForm.cardHolderRequired'): '') || '';
                  break;
                case 'CARD_DATE':
                  touched.cardDate = event?.CARD_DATE.touched;
                  errors.cardDate = event?.CARD_DATE.error || (!event?.CARD_DATE.touched ? t('paymentForm.cardExpiryRequired'): '') || '';
                  break;
                case 'CARD_CVV':
                  touched.cardCvv = event?.CARD_CVV.touched;
                  errors.cardCvv = event?.CARD_CVV.error || (!event?.CARD_CVV.touched ? t('paymentForm.cardCvvRequired'): '') || '';
                  break;
                case 'CARD_POSTAL_CODE':
                  touched.cardPostalCode = event?.CARD_POSTAL_CODE.touched;
                  errors.cardPostalCode = event?.CARD_POSTAL_CODE.error || (!event?.CARD_POSTAL_CODE.touched ? t('paymentForm.cardPostalRequired'): '') || '';
                  break;
                case 'CARD_STREET_ADDRESS':
                  touched.cardPostalCode = event?.CARD_STREET_ADDRESS.touched;
                  errors.cardStreetAddress = event?.CARD_STREET_ADDRESS.error || (!event?.CARD_STREET_ADDRESS.touched ? t('paymentForm.cardStreetRequired'): '') || '';
                  break;
                default:
                  break;
              }
    
              if (eventName == 'change') {
                errors.token = ''; // reset this error if fields changed
              }
    
            })
          });
        }

      }
    });

    // remove clover copyright
    let cloverFooter = document.querySelector('.clover-footer');
    if (cloverFooter instanceof HTMLElement) {
      document.body.removeChild(cloverFooter);
    }
  } else {
    console.error('🍀 Payments 🍀: instance is not ready for setup', clover.value, window['Clover']);
  }
}

onMounted(() => {
  cloverInstanceSetup();
});

onBeforeMount(() => {
    if ( window['Clover'] ) {
    clover.value = new window['Clover'](rootData.merchant.publicKey,
      {
        merchantId: rootData.merchant.id,
        cors: true,
      });
  } else {
    console.warn('🍀 Payments 🍀: window instance is not ready or script is not loaded')
  }
});
</script>

<template>
<div class="payment">
  <div class="paymentFieldset">
        <div v-for="[key, field] in visibleFields" :class="['cardInput', `${key}Input`]" :id="`${key}Input`"></div>
  </div>
  <div v-show="hasErrors" class="errors">
    <ErrorNote :message="errors.cardNumber || errors.cardDate || errors.cardCvv || errors.cardPostalCode || errors.token"/>
  </div>
</div>
</template>

<style lang="scss" scoped>
.paymentFieldset {
  display: grid;
  grid-template-columns: repeat(12, 1fr);
  grid-template-columns: repeat(12, minmax(0, 1fr));
  gap: 1rem;
}
.errors:not(:empty) {
  position: relative;
  .fieldError {
        margin-top: 8px;

      & div {
        margin-top: 0;
      }
    }
}
.cardInput {
  width: 100%;
  // height: 2.5rem;
  height: 2.75rem;
  box-sizing: inherit;
  position: relative;
  background-color: #f6f6f8;
  border: 1px solid #d6d9db;
  border-radius: 0.25rem;
  // padding: 0.375rem 0 0 0.5rem;
  padding: 0.375rem 0.5rem 0.375rem 0.5rem;
  margin: 0;
}
.cardNumberInput {
  grid-column: span 12;
  grid-column: 1 / -1;
}
.cardHolderNameInput {
  grid-column: span 12;
  grid-column: 1 / -1;
}
.cardDateInput {
  grid-column: span 4; /* zip layout */
  // grid-column: span 6; /* no zip layout */
}
.cardCvvInput {
  grid-column: span 4; /* zip layout */
  // grid-column: span 6; /* no zip layout */
}
.cardPostalCodeInput {
  grid-column: span 4;
}
.cardStreetAddressInput {
  grid-column: span 12;
  grid-column: 1 / -1;
}
</style>